import {
  Activity,
  AlertTriangle,
  BarChart3,
  BookOpenCheck,
  Bot,
  ChevronRight,
  CircleDollarSign,
  Gauge,
  LayoutDashboard,
  ListOrdered,
  LogIn,
  LogOut,
  Menu,
  RefreshCw,
  Settings,
  ShieldCheck,
  Target,
  WalletCards,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api, postJson, putJson } from "./api";
import ControlCenter from "./ControlCenter";
import StrategyPage from "./StrategyPage";
import { useSessionStore } from "./store";
import { summarizeOrders } from "./orderSummary";

type Row = Record<string, any>;
type DashboardPayload = {
  generatedAt?: string;
  degraded?: { name: string; status: number }[];
  system?: Row | null;
  candidates?: Row[];
  positions?: Row[];
  activeBatches?: Row[];
  closedBatches?: Row[];
  orders?: Row[];
  dailyReport?: Row | null;
  decisions?: Row[];
  focus?: Row | null;
};

const money = (value: unknown) => {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? `${Math.round(amount).toLocaleString("ko-KR")}원` : "-";
};

const pct = (value: unknown, digits = 2) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "-";
  return `${number > 0 ? "+" : ""}${number.toFixed(digits)}%`;
};

const num = (value: unknown, digits = 0) => {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString("ko-KR", { maximumFractionDigits: digits }) : "-";
};

const refreshTime = (value?: string) => {
  if (!value) return "대기 중";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "대기 중";
  return date.toLocaleTimeString("ko-KR", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const NAV = [
  { to: "/", label: "홈", icon: LayoutDashboard, end: true },
  { to: "/markets", label: "시장", icon: BarChart3 },
  { to: "/positions", label: "포지션", icon: WalletCards },
  { to: "/strategy", label: "전략", icon: Bot },
  { to: "/orders", label: "주문", icon: ListOrdered },
  { to: "/reports", label: "리포트", icon: BookOpenCheck },
  { to: "/control", label: "Control", icon: ShieldCheck },
  { to: "/settings", label: "설정", icon: Settings },
];

function tone(value: number) {
  return value > 0 ? "positive" : value < 0 ? "negative" : "neutral";
}

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return <span className={`status-dot ${ok ? "is-ok" : "is-off"}`}><i />{label}</span>;
}

function Skeleton({ rows = 4 }: { rows?: number }) {
  return <div className="skeleton-stack">{Array.from({ length: rows }, (_, i) => <div className="skeleton" key={i} />)}</div>;
}

function Empty({ title, body }: { title: string; body: string }) {
  return <div className="empty-state"><div className="empty-state__icon"><Target size={22} /></div><strong>{title}</strong><p>{body}</p></div>;
}

function Card({ title, eyebrow, action, children, className = "" }: { title: string; eyebrow?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}>
    <header className="panel__head"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2>{title}</h2></div>{action}</header>
    {children}
  </section>;
}

function Metric({ label, value, detail, valueTone = "neutral", icon }: { label: string; value: ReactNode; detail?: ReactNode; valueTone?: string; icon?: ReactNode }) {
  return <div className="metric-card">
    <div className="metric-card__top"><span>{label}</span>{icon && <i>{icon}</i>}</div>
    <strong className={`metric-card__value tone-${valueTone}`}>{value}</strong>
    {detail && <div className="metric-card__detail">{detail}</div>}
  </div>;
}

function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api<DashboardPayload>("/api/dashboard/bootstrap"),
    refetchInterval: 15_000,
    retry: 1,
  });
}

function LoginDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const login = useSessionStore((state) => state.login);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const username = String(form.get("username") || "admin");
    const password = String(form.get("password") || "");
    const remember = form.get("remember") === "on";
    setBusy(true);
    setError("");
    try {
      const result = await postJson<Row>("/api/admin/session", { username, password, remember_login: remember });
      login(String(result.username || username), Boolean(result.remember_login ?? remember));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "로그인하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
    <div className="modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
      <div className="modal__head"><div><span className="eyebrow">ADMIN ACCESS</span><h2>관리자 로그인</h2></div><button className="icon-button" onClick={onClose}><X size={18} /></button></div>
      <p className="muted">브라우저는 Worker에만 연결하며 관리자 세션은 FastAPI에서 최종 검증합니다.</p>
      <form className="stack" onSubmit={submit}>
        <label><span>아이디</span><input name="username" defaultValue="admin" autoComplete="username" /></label>
        <label><span>비밀번호</span><input name="password" type="password" autoComplete="current-password" autoFocus /></label>
        <label className="check-row"><input name="remember" type="checkbox" defaultChecked /><span>로그인 유지</span></label>
        {error && <div className="inline-error">{error}</div>}
        <button className="button primary" type="submit" disabled={busy}>{busy ? "확인 중…" : "로그인"}</button>
      </form>
    </div>
  </div>;
}

function Shell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const location = useLocation();
  const dashboard = useDashboard();
  const session = useSessionStore();

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    api<Row>("/api/admin/session")
      .then((data) => session.hydrate(String(data.username || ""), Boolean(data.authenticated), Boolean(data.remember_login)))
      .catch(() => session.logout());
  }, []);

  const system = dashboard.data?.system || {};
  const autoActive = Boolean(system.auto_trading_active ?? system.auto_trading);
  const autoRequested = Boolean(system.auto_trading_requested ?? autoActive);
  const tradingReady = Boolean(system.trading_ready ?? true);
  const autoLabel = autoActive ? "AUTO ACTIVE" : autoRequested ? (tradingReady ? "AUTO PAUSED" : "AUTO RECOVERING") : "AUTO OFF";
  const healthy = !dashboard.isError && Boolean(system);
  const pageTitle = NAV.find((item) => item.to === location.pathname)?.label || "홈";

  async function logout() {
    try {
      await api<Row>("/api/admin/session", { method: "DELETE" });
    } finally {
      session.logout();
    }
  }

  return <div className="app-shell">
    <aside className={`sidebar ${menuOpen ? "is-open" : ""}`}>
      <div className="brand-block"><div className="brand-mark">S</div><div><strong>Soltera Trader</strong><span>UPBIT OPERATIONS</span></div></div>
      <nav className="side-nav">
        {NAV.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `nav-item ${isActive ? "is-active" : ""}`}><Icon size={18} /><span>{label}</span><ChevronRight size={14} className="nav-item__arrow" /></NavLink>)}
      </nav>
      <div className="sidebar__footer"><StatusDot ok={healthy} label={healthy ? "API 정상" : "API 확인 필요"} /><StatusDot ok={!autoActive} label={autoLabel} /></div>
    </aside>
    {menuOpen && <button className="sidebar-scrim" aria-label="메뉴 닫기" onClick={() => setMenuOpen(false)} />}

    <section className="workspace">
      <header className="topbar">
        <div className="topbar__left"><button className="icon-button mobile-menu" onClick={() => setMenuOpen(true)}><Menu size={20} /></button><div><span className="eyebrow">SOLTERA / LIVE CONTROL</span><h1>{pageTitle}</h1></div></div>
        <div className="topbar__right">
          <div className="health-row"><StatusDot ok={healthy} label="시스템" /><StatusDot ok={!Boolean(system.kill_switch)} label={system.kill_switch ? "Kill 활성" : "Risk 정상"} /><StatusDot ok={!autoActive} label={autoLabel} /></div>
          <span className="refresh-status">15초 자동 · {refreshTime(dashboard.data?.generatedAt)} 갱신</span>
          <button
            className={`icon-button ${dashboard.isFetching ? "is-refreshing" : ""}`}
            title={dashboard.isFetching ? "새로고침 중" : "지금 새로고침"}
            aria-label={dashboard.isFetching ? "새로고침 중" : "지금 새로고침"}
            disabled={dashboard.isFetching}
            onClick={() => void dashboard.refetch()}
          ><RefreshCw size={18} /></button>
          {session.authenticated
            ? <button className="button soft" onClick={() => void logout()}><LogOut size={16} />{session.username || "관리자"} 로그아웃</button>
            : <button className="button primary" onClick={() => setLoginOpen(true)}><LogIn size={16} />로그인</button>}
        </div>
      </header>

      <main className="content"><Routes>
        <Route path="/" element={<DashboardPage query={dashboard} />} />
        <Route path="/markets" element={<MarketsPage />} />
        <Route path="/positions" element={<PositionsPage />} />
        <Route path="/strategy" element={<StrategyPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/control" element={<ControlCenter />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes></main>
    </section>
    <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
  </div>;
}

function DashboardPage({ query }: { query: ReturnType<typeof useDashboard> }) {
  if (query.isLoading) return <Skeleton rows={8} />;
  if (query.isError || !query.data) return <Empty title="운영 데이터를 불러오지 못했습니다" body="Worker와 Trading Origin 연결 상태를 확인한 뒤 다시 시도해 주세요." />;
  const data = query.data;
  const system = data.system || {};
  const report = data.dailyReport || {};
  const positions = data.positions || [];
  const candidates = data.candidates || [];
  const auto = Boolean(system.auto_trading_active ?? system.auto_trading);
  const totalAsset = Number(system.total_asset_krw || 0);
  const dailyPnl = Number(report.realized_pnl_krw ?? report.total_pnl_krw ?? 0);
  const dailyReturnValue = report.return_pct ?? report.total_return_pct;
  const dailyReturn = dailyReturnValue == null ? null : Number(dailyReturnValue);
  const cashflowStatus = String(report.cashflow_adjustment_status || "unverified_sync");
  const dailyReturnLabel = dailyReturn == null ? "검증 전" : pct(dailyReturn);
  const dailyReturnDetail = cashflowStatus === "complete"
    ? `입출금 보정 ${money(report.external_net_krw || 0)}`
    : "입출금 원시 기록 동기화 전";
  const regime = system.regime || {};

  return <div className="page-stack">
    {data.degraded?.length ? <div className="banner warning"><AlertTriangle size={18} /><span>일부 데이터를 가져오지 못했습니다: {data.degraded.map((item) => `${item.name}(${item.status})`).join(", ")}</span></div> : null}
    <section className="hero-card">
      <div><span className="eyebrow">CURRENT PORTFOLIO</span><h2>{totalAsset ? money(totalAsset) : "잔고 확인 전"}</h2><div className={`hero-return tone-${tone(dailyReturn ?? 0)}`}>{dailyReturnLabel} <span>{dailyPnl ? money(dailyPnl) : dailyReturnDetail}</span></div></div>
      <div className="hero-status"><div className={`system-state ${auto ? "is-live" : "is-safe"}`}><Activity size={20} /><div><span>자동매매</span><strong>{auto ? "실행 중" : "OFF"}</strong></div></div><p>{auto ? "신규 주문이 발생할 수 있습니다." : "분석과 모니터링만 수행합니다."}</p></div>
    </section>

    <section className="metric-grid">
      <Metric label="가용 현금" value={system.available_krw == null ? "확인 전" : money(system.available_krw)} icon={<CircleDollarSign size={18} />} />
      <Metric label="보유 포지션" value={`${positions.length}개`} detail={positions.length ? "보유 리스크 모니터링 중" : "현재 보유 없음"} icon={<WalletCards size={18} />} />
      <Metric label="시장 국면" value={String(regime.regime || "-").toUpperCase()} detail={`시장 변화 ${pct(regime.market_change_pct)}`} icon={<Gauge size={18} />} />
      <Metric label="매수 후보" value={`${candidates.length}개`} detail={candidates[0]?.market ? `최상위 ${candidates[0].market}` : "후보 없음"} icon={<Target size={18} />} />
    </section>

    <div className="two-column">
      <Card title="보유 포지션" eyebrow="PORTFOLIO" action={<NavLink className="text-link" to="/positions">전체 보기 <ChevronRight size={14} /></NavLink>}>
        {positions.length ? <PositionList rows={positions.slice(0, 5)} /> : <Empty title="보유 포지션 없음" body="자동매매가 OFF인 동안 신규 포지션은 생성되지 않습니다." />}
      </Card>
      <Card title="지금 주목할 시장" eyebrow="SIGNALS" action={<NavLink className="text-link" to="/markets">시장 보기 <ChevronRight size={14} /></NavLink>}>
        {candidates.length ? <CandidateList rows={candidates.slice(0, 6)} /> : <Empty title="매수 후보 없음" body="현재 필터를 통과한 종목이 없습니다." />}
      </Card>
    </div>

    <Card title="최근 주문" eyebrow="EXECUTION" action={<NavLink className="text-link" to="/orders">전체 주문 <ChevronRight size={14} /></NavLink>}>
      <OrderTable rows={(data.orders || []).slice(0, 8)} compact />
    </Card>
  </div>;
}

function PositionList({ rows }: { rows: Row[] }) {
  return <div className="list-stack">{rows.map((row, index) => {
    const ret = Number(row.return_pct ?? row.pnl_pct ?? row.unrealized_return_pct ?? 0);
    return <div className="position-row" key={row.market || index}><div><strong>{row.market || "-"}</strong><span>{row.strategy_source || row.strategy || "전략 확인 중"}</span></div><div className="position-row__middle"><span>평가</span><strong>{money(row.market_value_krw ?? row.valuation_krw ?? row.total_allocated_krw)}</strong></div><div className={`position-row__return tone-${tone(ret)}`}>{pct(ret)}</div><ChevronRight size={16} /></div>;
  })}</div>;
}

function CandidateList({ rows }: { rows: Row[] }) {
  return <div className="candidate-grid">{rows.map((row, index) => <div className="candidate-card" key={row.market || index}><div><strong>{row.market || "-"}</strong><span>#{index + 1}</span></div><b>{num(row.final_score ?? row.score, 1)}</b><p>{row.buy_blocked ? `차단 · ${row.block_reason || "리스크"}` : "진입 조건 평가 중"}</p></div>)}</div>;
}

function MarketsPage() {
  const query = useQuery({ queryKey: ["markets"], queryFn: () => api<Row[]>("/api/tickers?limit=100"), refetchInterval: 30_000 });
  const [search, setSearch] = useState("");
  const rows = useMemo(() => (query.data || []).filter((row) => String(row.market || "").toLowerCase().includes(search.toLowerCase()) || String(row.korean_name || "").includes(search)), [query.data, search]);
  return <div className="page-stack"><div className="page-intro"><div><span className="eyebrow">MARKET SCANNER</span><h2>전체 시장을 빠르게 탐색합니다</h2><p>매수 가능 여부보다 먼저 유동성·변동·현재 가격을 읽기 쉽게 정리했습니다.</p></div><input className="search-input" placeholder="BTC 또는 비트코인 검색" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
    <Card title="KRW 시장" eyebrow={`${rows.length} MARKETS`}>{query.isLoading ? <Skeleton /> : <div className="responsive-table"><table><thead><tr><th>종목</th><th>현재가</th><th>24h 변동</th><th>거래대금</th><th>상태</th></tr></thead><tbody>{rows.map((row) => <tr key={row.market}><td><strong>{row.market}</strong><small>{row.korean_name || row.english_name || ""}</small></td><td>{num(row.trade_price, 8)}</td><td className={`tone-${tone(Number(row.signed_change_rate || 0))}`}>{pct(Number(row.signed_change_rate || 0) * 100)}</td><td>{money(row.acc_trade_price_24h)}</td><td><span className="pill">모니터링</span></td></tr>)}</tbody></table></div>}</Card>
  </div>;
}

function PositionsPage() {
  const query = useQuery({ queryKey: ["positions"], queryFn: () => api<Row[]>("/api/positions"), refetchInterval: 15_000 });
  return <div className="page-stack"><div className="page-intro"><div><span className="eyebrow">PORTFOLIO</span><h2>포지션을 종목 중심으로 봅니다</h2><p>배치보다 먼저 현재 노출, 진입 전략과 손익을 확인할 수 있습니다.</p></div></div><Card title="보유 포지션" eyebrow={`${query.data?.length || 0} OPEN`}>{query.isLoading ? <Skeleton /> : query.data?.length ? <PositionList rows={query.data} /> : <Empty title="보유 포지션 없음" body="현재 실시간 보유 포지션이 없습니다." />}</Card></div>;
}

function OrdersPage() {
  const query = useQuery({
    queryKey: ["orders"],
    queryFn: async () => {
      const [orders, trades] = await Promise.all([
        api<Row[]>("/api/orders?limit=100"),
        api<Row[]>("/api/trades?limit=500"),
      ]);
      return summarizeOrders(orders, trades);
    },
    refetchInterval: 15_000,
  });
  const rows = query.data || [];
  const buys = rows.filter((row) => row.side === "bid");
  const sells = rows.filter((row) => row.side === "ask");
  return <div className="page-stack">
    <div className="page-intro"><div><span className="eyebrow">EXECUTION</span><h2>실제 체결 결과를 매수·매도로 나눠 확인합니다</h2><p>주문가격이 아니라 실제 체결수량·체결금액·평균 체결가와 수수료·실현손익을 표시합니다.</p></div></div>
    <Card title="매수" eyebrow={`${buys.length} BUY ORDERS`}>{query.isLoading ? <Skeleton /> : <ExecutionOrderTable rows={buys} side="bid" />}</Card>
    <Card title="매도" eyebrow={`${sells.length} SELL ORDERS`}>{query.isLoading ? <Skeleton /> : <ExecutionOrderTable rows={sells} side="ask" />}</Card>
  </div>;
}

function ExecutionOrderTable({ rows, side }: { rows: Row[]; side: "bid" | "ask" }) {
  if (!rows.length) return <Empty title={`${side === "bid" ? "매수" : "매도"} 기록 없음`} body="표시할 체결 주문이 없습니다." />;
  const isBuy = side === "bid";
  return <div className="responsive-table"><table><thead><tr>
    <th>종목</th><th>상태</th><th>총 수량</th><th>총 체결금액</th><th>평균 체결가</th>
    {isBuy ? <><th>매수 수수료</th><th>총 지출</th></> : <><th>실현손익</th><th>매도 수수료</th><th>실수령</th></>}
    <th>체결시각</th><th>전략</th>
  </tr></thead><tbody>{rows.map((row, index) => {
    const pnl = Number(row.realized_pnl_krw ?? 0);
    return <tr key={row.uuid || index}>
      <td><strong>{row.display_name && row.display_name !== row.market ? `${row.display_name} · ${row.market}` : row.market || "-"}</strong></td>
      <td>{row.state || "-"}</td>
      <td>{num(row.executed_volume ?? row.volume, 8)}개</td>
      <td>{money(row.executed_funds_krw)}</td>
      <td>{row.avg_executed_price == null ? "-" : money(row.avg_executed_price)}</td>
      {isBuy ? <><td>{money(row.paid_fee)}</td><td><strong>{money(row.total_cost_krw)}</strong></td></> : <><td className={`tone-${tone(pnl)}`}><strong>{row.realized_pnl_krw == null ? "-" : money(pnl)}</strong></td><td>{money(row.paid_fee)}</td><td><strong>{money(row.net_proceeds_krw)}</strong></td></>}
      <td>{row.created_at ? new Date(row.created_at).toLocaleString("ko-KR", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "-"}</td>
      <td>{row.strategy_source || "-"}</td>
    </tr>;
  })}</tbody></table></div>;
}

function OrderTable({ rows, compact = false }: { rows: Row[]; compact?: boolean }) {
  if (!rows.length) return <Empty title="주문 기록 없음" body="표시할 주문이 없습니다." />;
  return <div className="responsive-table"><table><thead><tr><th>종목</th><th>구분</th><th>상태</th><th>평균 체결가</th>{!compact && <><th>체결수량</th><th>체결금액</th></>}</tr></thead><tbody>{rows.map((row, index) => <tr key={row.uuid || index}><td><strong>{row.market || "-"}</strong></td><td><span className={`pill ${row.side === "bid" ? "success" : "danger"}`}>{row.side === "bid" ? "매수" : "매도"}</span></td><td>{row.state || "-"}</td><td>{row.avg_executed_price == null ? "-" : money(row.avg_executed_price)}</td>{!compact && <><td>{num(row.executed_volume ?? row.volume, 8)}개</td><td>{money(row.executed_funds_krw)}</td></>}</tr>)}</tbody></table></div>;
}

function ReportsPage() {
  const query = useQuery({ queryKey: ["report"], queryFn: () => api<Row>("/api/reports/daily"), refetchInterval: 60_000 });
  const report = query.data || {};
  const returnValue = report.return_pct ?? report.total_return_pct;
  const returnNumber = returnValue == null ? null : Number(returnValue);
  const cashflowStatus = String(report.cashflow_adjustment_status || "unverified_sync");
  return <div className="page-stack"><div className="page-intro"><div><span className="eyebrow">DAILY REVIEW</span><h2>오늘 결과를 요약해서 봅니다</h2><p>세부 로그보다 먼저 손익과 실행 결과를 확인합니다.</p></div></div><section className="metric-grid"><Metric label="실현 손익" value={money(report.realized_pnl_krw ?? report.total_pnl_krw)} valueTone={tone(Number(report.realized_pnl_krw ?? report.total_pnl_krw ?? 0))} /><Metric label="조정 수익률" value={returnNumber == null ? "검증 전" : pct(returnNumber)} detail={cashflowStatus === "complete" ? `입출금 보정 ${money(report.external_net_krw || 0)}` : "입출금 원시 기록 동기화 전"} valueTone={tone(returnNumber ?? 0)} /><Metric label="매수" value={`${num(report.buy_count)}회`} /><Metric label="매도" value={`${num(report.sell_count)}회`} /></section></div>;
}

function SettingsPage() {
  const session = useSessionStore();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    if (newPassword.length < 8) {
      setError("새 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호와 확인 값이 일치하지 않습니다.");
      return;
    }
    setBusy(true);
    try {
      await putJson<Row>("/api/admin/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      session.logout();
      setMessage("비밀번호를 변경했습니다. 모든 관리자 세션이 종료되었습니다. 새 비밀번호로 다시 로그인하세요.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "비밀번호를 변경하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return <div className="page-stack">
    <div className="page-intro"><div><span className="eyebrow">PREFERENCES</span><h2>설정은 일상 사용과 위험 설정을 분리합니다</h2><p>고위험 조작은 Control Center로 이동하고 이 화면은 표시·분석 환경을 중심으로 구성합니다.</p></div></div>
    <Card title="표시 환경" eyebrow="UI"><div className="setting-row"><div><strong>자동 새로고침</strong><span>Dashboard 15초 / Markets 30초</span></div><span className="pill success">사용</span></div><div className="setting-row"><div><strong>API 연결</strong><span>Worker same-origin /api</span></div><span className="pill success">고정</span></div></Card>
    <Card title="관리자 비밀번호" eyebrow="SECURITY">
      <form className="password-form" onSubmit={changePassword}>
        <p className="muted">비밀번호는 원문으로 저장하지 않고 단방향 해시로 보관합니다. 변경하면 기존 관리자 세션이 모두 종료됩니다.</p>
        <div className="password-form__grid">
          <label><span>현재 비밀번호</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required /></label>
          <label><span>새 비밀번호</span><input type="password" autoComplete="new-password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></label>
          <label><span>새 비밀번호 확인</span><input type="password" autoComplete="new-password" minLength={8} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></label>
        </div>
        {error && <div className="inline-error">{error}</div>}
        {message && <div className="inline-success">{message}</div>}
        <div className="action-row"><button className="button primary" type="submit" disabled={busy || !session.authenticated}>{busy ? "변경 중…" : "비밀번호 변경"}</button></div>
      </form>
    </Card>
  </div>;
}

export default Shell;
